import React from 'react'

import useCurrentMarketplaceId from 'src/components/presentationals/buyer-dashboard/shared/hooks/marketplaces/useCurrentMarketplaceId'
import { useMeQuery } from 'src/queries/seller/me'
import { useBuyerSubscriptionsQuery } from 'src/queries/shared/buyerSubscriptions/buyerSubscriptions'
import { useMarketplacesQuery } from 'src/queries/shared/marketplaces/marketplaces'
import { useSellerCompanyQuery } from 'src/queries/shared/sellerCompanies/sellerCompanies'
import { Membership } from 'src/types/membership'
import { UserType } from 'src/types/router'
import { User } from 'src/types/user'

import { useAuth } from './authContext'
import { useMembershipContext } from './MembershipContext'

const UserContext = React.createContext<User | undefined>(undefined)

export const UserProvider = (props: object) => {
  const {
    data: { user },
  } = useAuth()
  return <UserContext.Provider value={user} {...props} />
}

export const useUser = () => React.useContext(UserContext)

interface UseCurrentMembershipArgs<T extends boolean> {
  optional?: T
}

type UseCurrentMembershipResult<T> = T extends true ? Membership | undefined : Membership

export const useCurrentMembership = <T extends boolean = false>({
  optional,
}: UseCurrentMembershipArgs<T> = {}): UseCurrentMembershipResult<T> => {
  const membershipContext = useMembershipContext({ optional })

  return membershipContext?.currentMembership as Membership
}

interface GetUserTypeArgs {
  membership?: Membership
  user?: User
}

export const getUserType = ({ membership, user }: GetUserTypeArgs): UserType => {
  if (!user) return UserType.UNKNOWN
  if (user.isStaff) return UserType.ADMIN
  if (membership?.isBuyer) return UserType.BUYER
  if (membership?.isSeller) return UserType.SELLER

  return UserType.UNKNOWN
}

interface UseUserTypeArgs {
  optional?: boolean
}

interface UseUserTypeResult {
  isBuyer: boolean
  isSeller: boolean
  userType: UserType
}

export const useUserType = ({ optional }: UseUserTypeArgs = {}): UseUserTypeResult => {
  const user = useUser()
  const membership = useCurrentMembership({ optional })
  const userType = getUserType({ membership, user })

  return {
    isBuyer: userType === UserType.BUYER,
    isSeller: userType === UserType.SELLER,
    userType,
  }
}

interface UseUserBuyerCompanyIdArgs {
  optional?: boolean
}

export const useUserBuyerCompanyId = ({ optional }: UseUserBuyerCompanyIdArgs = {}):
  | Id
  | undefined => {
  const membership = useCurrentMembership({ optional })

  return (membership?.isBuyer && membership?.buyerCompanyId) || undefined
}

export const useBuyerBuyerCompanyId = (): Id => {
  const buyerCompanyId = useUserBuyerCompanyId()

  if (!buyerCompanyId) {
    throw new Error('buyerCompanyId must be defined')
  }

  return buyerCompanyId
}

interface UseUserSellerCompanyIdArgs {
  optional?: boolean
}

export const useUserSellerCompanyId = ({ optional }: UseUserSellerCompanyIdArgs = {}):
  | Id
  | undefined => {
  const membership = useCurrentMembership({ optional })

  return (membership?.isSeller && membership?.sellerCompanyId) || undefined
}

export const useSellerSellerCompanyId = (): Id => {
  const sellerCompanyId = useUserSellerCompanyId()

  if (!sellerCompanyId) {
    throw new Error('sellerCompanyId must be defined')
  }

  return sellerCompanyId
}

export const useSeller = () => {
  const sellerCompanyId = useUserSellerCompanyId()
  const { data: seller } = useMeQuery({ enabled: !!sellerCompanyId })

  return seller
}

export const useCurrentSellerCompany = () => {
  const sellerCompanyId = useUserSellerCompanyId()

  const { data: sellerCompany } = useSellerCompanyQuery({ id: sellerCompanyId })

  return sellerCompany
}

export const useOrganisationId = (): Id => {
  const membership = useCurrentMembership()
  return membership.organisationId
}

export const useUserDefaultLocationId = (): Id | undefined => {
  const membership = useCurrentMembership()

  return membership.defaultLocationId ?? undefined
}

export const useCurrentMarketplace = (marketplaceId?: Id) => {
  const currentMarketplaceId = useCurrentMarketplaceId() ?? marketplaceId

  const { data: marketplace } = useMarketplacesQuery({
    queryOptions: {
      select: marketplaces => marketplaces.find(({ id }) => id === currentMarketplaceId),
    },
  })

  return marketplace
}

export const useActiveBuyerSubscription = (marketplaceId?: Id) => {
  const buyerCompanyId = useBuyerBuyerCompanyId()
  const { data: marketplaces = [] } = useMarketplacesQuery()
  const currentMarketplaceId = useCurrentMarketplaceId() ?? marketplaceId

  const { data: buyerSubscription } = useBuyerSubscriptionsQuery({
    queryParams: {
      companyId: buyerCompanyId,
      marketplaceId: marketplaces.map(({ id }) => id),
    },
    queryOptions: {
      select: buyerSubscriptions =>
        buyerSubscriptions.find(
          subscription =>
            subscription.companyId === buyerCompanyId &&
            subscription.marketplaceId === currentMarketplaceId
        ),
    },
  })

  return buyerSubscription
}

export const useConnectedBuyerMembership = () => {
  const user = useUser()
  const currentmembership = useCurrentMembership()

  const buyerMembership = user?.memberships.find(
    membership =>
      currentmembership.isSeller &&
      membership.isBuyer &&
      membership.organisationId === currentmembership.organisationId
  )

  return buyerMembership
}
